import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

const TopThreeContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  gap: '24px',
  marginBottom: '32px',
  padding: '0 16px',
});

const PlayerCardSkeleton = styled(Box)<{ isLarge?: boolean }>(
  ({ isLarge }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    width: isLarge ? '160px' : '120px',
  })
);

const TableContainer = styled(Box)({
  padding: '16px',
});

const TableRowSkeleton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  gap: '16px',
  borderRadius: '8px',
  marginBottom: '8px',
});

const GameDetailsSkeleton = () => {
  const theme = useTheme();

  return (
    <>
      {/* Header */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Skeleton
          variant="text"
          width={200}
          height={40}
          sx={{ mx: 'auto', mb: 1, bgcolor: theme.custom.background3 }}
        />
        <Skeleton
          variant="text"
          width={160}
          height={24}
          sx={{ mx: 'auto', bgcolor: theme.custom.background3 }}
        />
      </Box>

      {/* Top Three Players */}
      <TopThreeContainer>
        {/* Second Place */}
        <PlayerCardSkeleton>
          <Skeleton
            variant="circular"
            width={80}
            height={80}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={40}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={60}
            height={20}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={40}
            height={20}
            sx={{ bgcolor: theme.custom.background3 }}
          />
        </PlayerCardSkeleton>

        {/* First Place */}
        <PlayerCardSkeleton isLarge>
          <Skeleton
            variant="circular"
            width={120}
            height={120}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={40}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={80}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={50}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
        </PlayerCardSkeleton>

        {/* Third Place */}
        <PlayerCardSkeleton>
          <Skeleton
            variant="circular"
            width={80}
            height={80}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={40}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={60}
            height={20}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={40}
            height={20}
            sx={{ bgcolor: theme.custom.background3 }}
          />
        </PlayerCardSkeleton>
      </TopThreeContainer>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: theme.custom.stroke, mb: 2 }}>
        <Box sx={{ display: 'flex', px: 2, gap: 2 }}>
          <Skeleton
            variant="text"
            width={60}
            height={40}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={60}
            height={40}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={60}
            height={40}
            sx={{ bgcolor: theme.custom.background3 }}
          />
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Skeleton
          variant="rectangular"
          height={40}
          sx={{ borderRadius: 1, bgcolor: theme.custom.background3 }}
        />
      </Box>

      {/* Table */}
      <TableContainer>
        {/* Table Header */}
        <TableRowSkeleton sx={{ backgroundColor: theme.custom.background2 }}>
          <Skeleton
            variant="text"
            width={40}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={200}
            height={24}
            sx={{ flex: 1, bgcolor: theme.custom.background3 }}
          />
          <Skeleton
            variant="text"
            width={80}
            height={24}
            sx={{ bgcolor: theme.custom.background3 }}
          />
        </TableRowSkeleton>

        {/* Table Rows */}
        {[...Array(5)].map((_, index) => (
          <TableRowSkeleton
            key={index}
            sx={{
              backgroundColor:
                index % 2 === 0
                  ? theme.custom.background2
                  : theme.custom.background1,
            }}
          >
            <Skeleton
              variant="text"
              width={40}
              height={24}
              sx={{ bgcolor: theme.custom.background3 }}
            />
            <Box
              sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}
            >
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ bgcolor: theme.custom.background3 }}
              />
              <Skeleton
                variant="text"
                width={160}
                height={24}
                sx={{ bgcolor: theme.custom.background3 }}
              />
            </Box>
            <Skeleton
              variant="text"
              width={80}
              height={24}
              sx={{ bgcolor: theme.custom.background3 }}
            />
          </TableRowSkeleton>
        ))}
      </TableContainer>
    </>
  );
};

export default GameDetailsSkeleton;
